<template>
    <div>
        <DataTable
            :data="invoices"
            :columns="[
                {
                    title: 'Invoice #',
                    field: 'invoice_number',
                    class: 'tw-whitespace-nowrap',
                },
                {
                    title: 'Title',
                    field: 'title',
                },
                {
                    title: 'Subtotal',
                    field: 'subtotal',
                    headerClass: 'tw-text-right',
                    class: 'tw-text-right',
                },
                {
                    title: 'Tax',
                    field: 'tax_amount',
                    headerClass: 'tw-text-right',
                    class: 'tw-text-right',
                },
                {
                    title: 'Total',
                    field: 'total_value',
                    headerClass: 'tw-text-right',
                    class: 'tw-text-right',
                },
                {
                    title: '',
                    field: 'meta',
                },
                {
                    title: '',
                    field: 'actions',
                    class: 'tw-text-right',
                },
            ]"
            :row-expandable="(row) => row.file_uploads && row.file_uploads.length > 0"
            :row-class="(row) => (isMultiPOInvoice(row) ? 'tw-bg-orange-50' : '')"
        >
            <template #column(invoice_number)="{ row }">
                {{ row.invoice_number }}
                <small class="tw-block tw-text-gray-400">{{ formatDate(row.invoice_date, 'dd/MM/yyyy') }}</small>
            </template>
            <template #column(title)="{ row }">
                {{ row.title }}
            </template>
            <template #column(subtotal)="{ row }">
                <Tooltip
                    :label="formatCurrency(subtotal(row, false), 2, row.base_currency)"
                    :disabled="row.base_currency == row.target_currency"
                    class="tw-w-full tw-text-right"
                >
                    <span>{{ formatCurrency(subtotal(row), 2, row.target_currency) }}</span>
                </Tooltip>
            </template>
            <template #column(tax_amount)="{ row }">
                <Tooltip
                    :label="formatCurrency(taxAmount(row, false), 2, row.base_currency)"
                    :disabled="row.base_currency == row.target_currency"
                    class="tw-w-full tw-text-right"
                >
                    <span>{{ formatCurrency(taxAmount(row), 2, row.target_currency) }}</span>
                </Tooltip>
            </template>
            <template #column(total_value)="{ row }">
                <Tooltip :disabled="row.base_currency == row.target_currency" class="tw-w-full tw-text-right">
                    <template #content>
                        {{ formatCurrency(totalValue(row, false), 2, row.base_currency) }}
                    </template>
                    <span>{{ formatCurrency(totalValue(row), 2, row.target_currency) }}</span>
                    <div
                        v-if="parseFloat(row.total_credit) || parseFloat(row.total_credit_target)"
                        class="tw-text-xs tw-text-gray-500 tw-whitespace-nowrap"
                    >
                        (CR
                        {{
                            formatCurrency(
                                row.total_credit_target ? row.total_credit_target : row.total_credit,
                                2,
                                row.target_currency ? row.target_currency : row.base_currency
                            )
                        }})
                    </div>
                </Tooltip>
            </template>
            <template #column(meta)="{ row }">
                <Tooltip class="tw-mx-auto" label="Attachments">
                    <div class="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-gray-500">
                        <i class="mdi mdi-attachment tw-block tw-scale-125 -tw-rotate-45" />
                        {{ row.file_uploads?.length }}
                    </div>
                </Tooltip>
            </template>
            <template #column(actions)="{ row }">
                <Button @click="viewInvoice(row)">
                    <i class="mdi mdi-eye"></i>
                </Button>
            </template>
            <template #expanded-row="{ row: expandedRow }">
                <DataTable
                    v-if="expandedRow.file_uploads?.length"
                    class="!tw-bg-slate-50"
                    :columns="[
                        {
                            title: 'Attached Files',
                            field: 'file_name',
                        },
                        {
                            title: 'Size',
                            field: 'file_size',
                        },
                        {
                            title: '',
                            field: 'actions',
                            class: '!tw-text-right',
                        },
                    ]"
                    :data="expandedRow.file_uploads"
                >
                    <template #column(file_name)="{ row }">
                        {{ row.file_name }}
                    </template>
                    <template #column(file_size)="{ row }">{{ bytes2mb(row.file_size) }}MB</template>
                    <template #column(actions)="{ row }">
                        <Button @click="downloadAttachment(row)">
                            <i class="mdi mdi-download"></i>
                        </Button>
                    </template>
                </DataTable>
            </template>
        </DataTable>
        <div v-if="hasMultiPOInvoices" class="tw-border-b tw-px-2 tw-pb-2">
            <span class="tw-font-medium tw-text-[.66rem] tw-text-gray-400">Table Legend</span>
            <div class="tw-flex tw-gap-2">
                <div class="tw-w-4 tw-h-4 tw-bg-orange-50 tw-rounded tw-border tw-border-orange-200"></div>
                <span class="tw-text-xs tw-text-gray-400">Invoice is linked to multiple purchase orders</span>
            </div>
        </div>
        <Button
            v-if="!unableToRaiseInvoiceMessage"
            type="is-link"
            class="tw-w-full tw-flex tw-gap-2 tw-justify-center tw-py-2"
            @click="onRaiseInvoice()"
        >
            <i class="mdi mdi-plus"></i>
            Raise an invoice
        </Button>
        <p v-else class="empty-label tw-py-2" v-html="unableToRaiseInvoiceMessage"></p>
    </div>
</template>
<script setup>
import Button from '@/js/components/controls/Button.vue';
import DataTable from '@/js/components/tables/DataTable.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import { formatCurrency, formatDate } from '@/js/utils.js';
import { computed } from 'vue';

const props = defineProps({
    invoices: {
        type: Array,
        default: () => [],
    },
    onRaiseInvoice: {
        type: Function,
        required: true,
    },
    canRaiseInvoiceConditions: {
        type: Array,
        default: () => [
            {
                can: false,
            },
        ],
    },
    canCreateInvoice: {
        type: Boolean,
        required: true,
    },
});

const unableToRaiseInvoiceMessage = computed(() => {
    const failedCondition = props.canRaiseInvoiceConditions.find((condition) => !condition.can);
    if (failedCondition) {
        return failedCondition.message;
    }

    if (!props.canCreateInvoice) {
        return 'You do not have permission to raise invoices - please contact your account owner to obtain permission.';
    }
    return null;
});

const emit = defineEmits(['view']);

const hasMultiPOInvoices = computed(() => {
    return !!props.invoices.filter((invoice) => isMultiPOInvoice(invoice)).length;
});

function downloadAttachment(fileUpload) {
    if (!fileUpload.download_url) {
        return;
    }
    window.open(fileUpload.download_url, '_blank');
}

function modifiedSubtotal(modifier, subtotal) {
    return (parseFloat(modifier) / 100) * subtotal;
}

function viewInvoice(row) {
    emit('view', row.id);
}

function bytes2kb(bytes) {
    return (parseFloat(bytes) / 1000).toFixed(1);
}

function bytes2mb(bytes) {
    return (parseFloat(bytes2kb(bytes)) / 1000).toFixed(1);
}

function isMultiPOInvoice(row) {
    return row.pivot && parseFloat(row.pivot.value_modifier) < 100;
}

const subtotal = (invoice, target = true) => {
    // This is required, need to check the !!invoice[`key`] is set as customer invoice wont have this value
    // Keeping the logic here because otherwise it would just be spread elsewhere in the file with multiple potential points of failure
    const subtotalKey = target && !!invoice.subtotal_target ? 'subtotal_target' : 'subtotal';
    if (invoice.pivot && invoice.pivot.value_modifier < 100) {
        return modifiedSubtotal(invoice.pivot.value_modifier, invoice[subtotalKey]);
    }
    return invoice[subtotalKey];
};
const taxAmount = (invoice, target = true) => {
    // This is required, need to check the !!invoice[`key`] is set as customer invoice wont have this value
    // Keeping the logic here because otherwise it would just be spread elsewhere in the file with multiple potential points of failure
    const taxKey = target && !!invoice.tax_amount_target ? 'tax_amount_target' : 'tax_amount';
    if (invoice.pivot && invoice.pivot.value_modifier < 100) {
        return modifiedSubtotal(invoice.pivot.value_modifier, invoice[taxKey]);
    }
    return invoice[taxKey];
};

const totalValue = (invoice, target = true) => {
    // This is required, need to check the !!invoice[`key`] is set as customer invoice wont have this value
    // Keeping the logic here because otherwise it would just be spread elsewhere in the file with multiple potential points of failure
    const totalKey = target && !!invoice.total_value_target ? 'total_value_target' : 'total_value';
    const creditKey = target && !!invoice.total_credit_target ? 'total_credit_target' : 'total_credit';
    const value = invoice[totalKey] - (invoice[creditKey] || 0);
    if (invoice.pivot && invoice.pivot.value_modifier < 100) {
        return modifiedSubtotal(invoice.pivot.value_modifier, value);
    }
    return value;
};
</script>
